//similar application component which we implemented the feature

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  collection, query, where, getDocs, orderBy, doc,
  updateDoc, onSnapshot, addDoc
} from "firebase/firestore";
import { db } from "../firebase-config";
import html2pdf from "html2pdf.js";
import emailjs from '@emailjs/browser';
import {
  Dialog, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Card, CardContent,
  LinearProgress, TextField, IconButton, Tooltip, Typography, Button,
  Box, Select, MenuItem, FormControl, InputLabel, alpha, Chip, Stack,
  Divider, useMediaQuery, InputAdornment, Grid, Collapse, Fade,
  CircularProgress, Snackbar, Alert, styled
} from "@mui/material";
import {
  Search, Download, ChevronRight, X, ArrowLeft, Calendar,
  Building2, Mail, Receipt, CreditCard, Printer, Filter,
  ChevronDown, ChevronUp, Eye, Send, CheckCircle, Warning
} from "lucide-react";
import { Phone, Business, Info } from "@mui/icons-material";
import Header from "../components/header";
import LOGO from "../assets/abLogo.svg";
import appTheme from "../theme";
import moment from "moment";

// **STYLED COMPONENTS WITH LUXURY THEME**
const PageContainer = styled(Box)({
  padding: appTheme.spacing.lg,
  backgroundColor: appTheme.colors.surfaceLuxury,
  minHeight: "100vh",
});

const StyledCard = styled(Card)({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.medium,
  border: `1px solid ${appTheme.colors.borderSubtle}`,
  transition: appTheme.transitions.luxury,
  backgroundColor: appTheme.colors.surface,
  "&:hover": {
    boxShadow: appTheme.shadows.strong,
    transform: "translateY(-2px)",
  }
});

const HeaderCard = styled(StyledCard)({
  background: appTheme.gradients.hero,
  color: appTheme.colors.champagne,
  marginBottom: appTheme.spacing.xl,
});

const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: appTheme.borderRadius.medium,
    backgroundColor: appTheme.colors.surface,
    boxShadow: appTheme.shadows.whisper,
    "& fieldset": {
      borderColor: appTheme.colors.borderPrimary,
    },
    "&:hover fieldset": {
      borderColor: appTheme.colors.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: appTheme.colors.primary,
      borderWidth: "2px",
    },
  },
});

const ActionButton = styled(Button)(({ variant, color }) => ({
  borderRadius: appTheme.borderRadius.medium,
  textTransform: "none",
  fontWeight: appTheme.typography.fontWeights.semibold,
  padding: `${appTheme.spacing.sm} ${appTheme.spacing.lg}`,
  boxShadow: variant === "contained" ? appTheme.shadows.soft : "none",
  transition: appTheme.transitions.luxury,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: variant === "contained" ? appTheme.shadows.medium : appTheme.shadows.soft,
  }
}));

const StyledTableContainer = styled(TableContainer)({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.medium,
  border: `1px solid ${appTheme.colors.borderPrimary}`,
  backgroundColor: appTheme.colors.surface,
});

const StyledTableHead = styled(TableHead)({
  background: appTheme.gradients.hero,
  "& .MuiTableCell-head": {
    color: appTheme.colors.champagne,
    fontWeight: appTheme.typography.fontWeights.bold,
    fontSize: appTheme.typography.fontSizes.small,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
  }
});

const StatusChip = styled(Chip)(({ status }) => ({
  fontWeight: appTheme.typography.fontWeights.semibold,
  borderRadius: appTheme.borderRadius.soft,
  ...(status === "Paid" && {
    backgroundColor: alpha(appTheme.colors.success, 0.1),
    color: appTheme.colors.success,
  }),
  ...(status === "Pending" && {
    backgroundColor: alpha(appTheme.colors.warning, 0.1),
    color: appTheme.colors.warning,
  }),
  ...(status === "Overdue" && {
    backgroundColor: alpha(appTheme.colors.error, 0.1),
    color: appTheme.colors.error,
  }),
}));

const ConsolidatedInvoiceView = () => {
  // **STATE MANAGEMENT**
  const [restaurants, setRestaurants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [consolidatedInvoices, setConsolidatedInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoiceGroup, setSelectedInvoiceGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("Pending");
  const [isPrinting, setIsPrinting] = useState(false);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [periodType, setPeriodType] = useState('week');
  const [fromDate, setFromDate] = useState(null); // store as JS Date or ISO string
  const [toDate, setToDate] = useState(null);
  const [discountType, setDiscountType] = useState('none'); // 'none' | 'percent' | 'amount'
  const [discountValue, setDiscountValue] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [originalAmount, setOriginalAmount] = useState(0);
  const [rowSaveStatus, setRowSaveStatus] = useState({});
  const [editingReason, setEditingReason] = useState({});


  // **EMAIL FUNCTIONALITY STATE**
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ show: false, message: "", type: "success" });
  const [emailDialog, setEmailDialog] = useState({ open: false, email: "", message: "" });
  const [rowDiscounts, setRowDiscounts] = useState({});

  const invoiceRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  // **SAFE HELPERS**
  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  const calculateDiscountedTotal = (originalTotal = 0, discType = 'none', discValue = '') => {
    if (discType === 'none' || !discValue) return originalTotal
    const discount = parseFloat(discValue) || 0
    if (discType === 'percent') return originalTotal * (1 - discount / 100)
    return originalTotal - discount
  }


  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  // **DATE FORMATTING FUNCTIONS**
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return moment(date).format("MMM D, YYYY");
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return moment(date).format("h:mm A");
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return moment(date).format("MMM D, YYYY h:mm A");
  };

  // invoices: full list loaded from Firestore for the selected restaurant
  // periodType: 'fortnight' | 'month' | 'custom'
  const filterInvoicesByPeriod = (invoices, periodType, fromDate, toDate) => {
    if (!Array.isArray(invoices) || invoices.length === 0) return [];

    if (periodType === 'custom' && fromDate && toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return invoices.filter(inv => {
        const ts = inv.createdAt;
        const date = ts?.toDate ? ts.toDate() : new Date(ts);
        return date >= start && date <= end;
      });
    }

    if (periodType === 'month') {
      const now = new Date();
      const targetYear = now.getFullYear();
      const targetMonth = now.getMonth(); // 0-based

      return invoices.filter(inv => {
        const ts = inv.createdAt;
        const date = ts?.toDate ? ts.toDate() : new Date(ts);
        return date.getFullYear() === targetYear && date.getMonth() === targetMonth;
      });
    }

    // Default: keep all (your old behaviour) for fortnight
    return invoices;
  };


  // **PROFESSIONAL EMAIL TEMPLATE**
  const generateEmailTemplate = (invoiceGroup, restaurant) => {
    const invoiceNumber = `ABF-${invoiceGroup.year}-${invoiceGroup.month.slice(0, 3).toUpperCase()}-${invoiceGroup.period === "1-15" ? "A" : "B"}`;
    const totalAmount = safeToFixed(invoiceGroup.totalAmount);
    const periodText = `${formatDate(invoiceGroup.startDate)} - ${formatDate(invoiceGroup.endDate)}`;

    return {
      subject: `📧Consolidated Invoice ${invoiceNumber} - Crispydosa Kitchen Limited`,

      htmlContent: `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Consolidated Invoice</title>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { 
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                    line-height: 1.6; 
                    color: #333; 
                    background-color: #f8f9fa; 
                }
                .container { 
                    max-width: 800px; 
                    margin: 20px auto; 
                    background: white; 
                    border-radius: 16px; 
                    overflow: hidden; 
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1); 
                }
                .header { 
                    background: linear-gradient(135deg, #1a4d3a 0%, #8b1538 100%); 
                    color: white; 
                    padding: 40px 30px; 
                    text-align: center; 
                }
                .header h1 { font-size: 28px; margin-bottom: 10px; font-weight: 700; }
                .header p { font-size: 16px; opacity: 0.9; }
                .content { padding: 40px 30px; }
                .invoice-header { 
                    display: flex; 
                    justify-content: space-between; 
                    margin-bottom: 40px; 
                    flex-wrap: wrap; 
                }
                .bill-from, .bill-to { 
                    background: #fcffe9; 
                    padding: 25px; 
                    border-radius: 12px; 
                    border: 2px solid #e6f2e6; 
                    margin-bottom: 20px; 
                    flex: 1; 
                    min-width: 280px; 
                }
                .bill-from { margin-right: 20px; }
                .bill-from h3, .bill-to h3 { 
                    color: #1a4d3a; 
                    margin-bottom: 15px; 
                    font-size: 18px; 
                    font-weight: 700; 
                }
                .invoice-details { 
                    background: linear-gradient(135deg, #f7e7ce 0%, #faf6e8 100%); 
                    padding: 25px; 
                    border-radius: 12px; 
                    margin-bottom: 40px; 
                    border: 2px solid #d4af37; 
                }
                .detail-row { 
                    display: flex; 
                    justify-content: space-between; 
                    margin-bottom: 10px; 
                    align-items: center; 
                }
                .detail-label { 
                    font-weight: 600; 
                    color: #1a4d3a; 
                    font-size: 16px; 
                }
                .detail-value { 
                    font-weight: 700; 
                    color: #8b1538; 
                    font-size: 16px; 
                }
                .invoice-table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    margin: 30px 0; 
                    border-radius: 12px; 
                    overflow: hidden; 
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1); 
                }
                .invoice-table th { 
                    background: linear-gradient(135deg, #1a4d3a 0%, #4CAF50 100%); 
                    color: white; 
                    padding: 18px 15px; 
                    text-align: left; 
                    font-weight: 700; 
                    font-size: 14px; 
                    text-transform: uppercase; 
                    letter-spacing: 0.5px; 
                }
                .invoice-table td { 
                    padding: 15px; 
                    border-bottom: 1px solid #e0e0e0; 
                    font-size: 15px; 
                }
                .invoice-table tr:nth-child(even) { background-color: #fcffe9; }
                .invoice-table tr:hover { background-color: #e6f2e6; }
                .total-row { 
                    background: linear-gradient(135deg, #1a4d3a 0%, #4CAF50 100%) !important; 
                    color: white !important; 
                    font-weight: 700 !important; 
                    font-size: 18px !important; 
                }
                .total-row td { 
                    color: white !important; 
                    border-bottom: none !important; 
                    padding: 20px 15px !important; 
                }
                .payment-status { 
                    display: inline-block; 
                    padding: 8px 16px; 
                    border-radius: 25px; 
                    font-weight: 600; 
                    font-size: 14px; 
                    text-transform: uppercase; 
                    letter-spacing: 0.5px; 
                }
                .status-paid { 
                    background: #d4edda; 
                    color: #155724; 
                    border: 2px solid #c3e6cb; 
                }
                .status-pending { 
                    background: #fff3cd; 
                    color: #856404; 
                    border: 2px solid #ffeaa7; 
                }
                .footer { 
                    background: #f8f9fa; 
                    padding: 30px; 
                    text-align: center; 
                    border-top: 3px solid #1a4d3a; 
                }
                .footer p { 
                    margin-bottom: 10px; 
                    color: #666; 
                    font-size: 14px; 
                }
                .highlight { 
                    background: linear-gradient(120deg, #d4af37 0%, transparent 100%); 
                    padding: 2px 8px; 
                    border-radius: 4px; 
                    font-weight: 600; 
                }
                .amount-highlight { 
                    color: #8b1538; 
                    font-size: 24px; 
                    font-weight: 700; 
                }
                @media (max-width: 600px) {
                    .container { margin: 10px; border-radius: 12px; }
                    .content { padding: 20px 15px; }
                    .invoice-header { flex-direction: column; }
                    .bill-from { margin-right: 0; margin-bottom: 20px; }
                    .detail-row { flex-direction: column; align-items: flex-start; }
                    .detail-value { margin-top: 5px; }
                    .invoice-table { font-size: 12px; }
                    .invoice-table th, .invoice-table td { padding: 10px 8px; }
                }
            </style>
        </head>
        <body>
            <div class="container">
                <div class="header">
                    <h1>🏢 Consolidated Invoice</h1>
                    <p>Professional Billing Statement from Crispydosa Kitchen Limited</p>
                </div>
                
                <div class="content">
                    <div class="invoice-header">
                        <div class="bill-from">
                            <h3>📍 Bill From:</h3>
                            <p><strong>Crispydosa Kitchen Limited</strong></p>
                            <p>20, Portman Road Reading</p>
                            <p>RG30 1Ea</p>
                            <p>United Kingdom</p>
                            <p>📧 Email: cdcentralkitchen@gmail.com</p>
                        </div>
                        
                        <div class="bill-to">
                            <h3>📍 Bill To:</h3>
                            <p><strong>${restaurant.restaurantName}</strong></p>
                            <p>${restaurant.address || 'N/A'}</p>
                            <p>📞 ${restaurant.phone || 'N/A'}</p>
                            <p>📧 ${restaurant.email || 'N/A'}</p>
                        </div>
                    </div>
                    
                    <div class="invoice-details">
                        <div class="detail-row">
                            <span class="detail-label">📄 Invoice Number:</span>
                            <span class="detail-value highlight">${invoiceNumber}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">📅 Billing Period:</span>
                            <span class="detail-value">${periodText}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">📅 Invoice Date:</span>
                            <span class="detail-value">${moment().format('MMMM D, YYYY')}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">💳 Payment Status:</span>
                            <span class="payment-status ${paymentStatus === 'Paid' ? 'status-paid' : 'status-pending'}">
                                ${paymentStatus === 'Paid' ? '✅ Paid' : '⏳ Pending'}
                            </span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">💰 Total Amount:</span>
                            <span class="detail-value amount-highlight">£${totalAmount}</span>
                        </div>
                    </div>
                    
                    <table class="invoice-table">
                        <thead>
                            <tr>
                                <th>📅 Date</th>
                                <th>📄 Invoice #</th>
                                <th>📦 Items</th>
                                <th>💷 Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${invoiceGroup.invoices.map(invoice => `
                                <tr>
                                    <td>${formatDate(invoice.createdAt)}</td>
                                    <td>#${invoice.id.slice(0, 8)}...</td>
                                    <td>${invoice.items?.length || 0} items</td>
                                    <td>£${safeToFixed(invoice.totalPrice)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                        <tfoot>
                            <tr class="total-row">
                                <td colspan="2"><strong>📊 TOTAL</strong></td>
                                <td><strong>${invoiceGroup.invoices.reduce((sum, inv) => sum + (inv.items?.length || 0), 0)} items</strong></td>
                                <td><strong>£${totalAmount}</strong></td>
                            </tr>
                        </tfoot>
                    </table>
                    
                    <div style="background: #e8f5e8; padding: 25px; border-radius: 12px; border: 2px solid #4CAF50; margin-top: 30px;">
                        <h3 style="color: #1a4d3a; margin-bottom: 15px;">💡 Payment Information</h3>
                        <p style="margin-bottom: 10px;">📧 <strong>Contact:</strong> billing@aryabhavan.co.uk</p>
                        <p style="margin-bottom: 10px;">📞 <strong>Phone:</strong> +44 20 8XXX XXXX</p>
                        <p>⏰ <strong>Payment Terms:</strong> Net 30 days from invoice date</p>
                    </div>
                </div>
                
                <div class="footer">
                    <p><strong>🙏 Thank you for your business!</strong></p>
                    <p>This is a computer-generated document. For any queries, please contact us.</p>
                    <p style="font-size: 12px; color: #999; margin-top: 15px;">
                        © ${new Date().getFullYear()} Crispydosa Kitchen Limited. All rights reserved.
                    </p>
                </div>
            </div>
        </body>
        </html>
      `,

      textContent: `
        CONSOLIDATED INVOICE - ${invoiceNumber}
        
        Bill From:
        Crispydosa Kitchen Limited
        20, Portman Road Reading, RG30 1Ea, United Kingdom
        
        Bill To:
        ${restaurant.restaurantName}
        ${restaurant.address || 'N/A'}
        Phone: ${restaurant.phone || 'N/A'}
        Email: ${restaurant.email || 'N/A'}
        
        Invoice Details:
        - Invoice Number: ${invoiceNumber}
        - Billing Period: ${periodText}
        - Invoice Date: ${moment().format('MMMM D, YYYY')}
        - Payment Status: ${paymentStatus}
        - Total Amount: £${totalAmount}
        
        Invoice Items:
        ${invoiceGroup.invoices.map(invoice =>
        `- ${formatDate(invoice.createdAt)} | #${invoice.id.slice(0, 8)} | ${invoice.items?.length || 0} items | £${safeToFixed(invoice.totalPrice)}`
      ).join('\n')}
        
        TOTAL: £${totalAmount}
        
        Thank you for your business!
        Crispydosa Kitchen Limited
      `
    };
  };

  // **EMAIL SENDING FUNCTIONALITY**
  // const sendConsolidatedInvoice = async (email, message = "") => {
  //   if (!selectedInvoiceGroup || !selectedRestaurant) {
  //     setEmailStatus({
  //       show: true,
  //       message: "No invoice selected to send",
  //       type: "error"
  //     });
  //     return;
  //   }

  //   setEmailSending(true);
  //   try {
  //     // Initialize EmailJS (you need to set up your EmailJS account)
  //     emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "YOUR_EMAILJS_PUBLIC_KEY");

  //     const template = generateEmailTemplate(selectedInvoiceGroup, selectedRestaurant);

  //     const emailParams = {
  //       to_email: email || selectedRestaurant.email,
  //       to_name: selectedRestaurant.restaurantName,
  //       from_name: "Arya Bhavan Central Kitchen",
  //       subject: template.subject,
  //       html_content: template.htmlContent,
  //       text_content: template.textContent,
  //       custom_message: message,
  //       invoice_number: `ABF-${selectedInvoiceGroup.year}-${selectedInvoiceGroup.month.slice(0, 3).toUpperCase()}-${selectedInvoiceGroup.period === "1-15" ? "A" : "B"}`,
  //       total_amount: safeToFixed(groupDiscountedTotal, 0) || 0),
  //       billing_period: `${formatDate(selectedInvoiceGroup.startDate)} - ${formatDate(selectedInvoiceGroup.endDate)}`,
  //     };

  //     // Send email using EmailJS
  //     const result = await emailjs.send(
  //       process.env.REACT_APP_EMAILJS_SERVICE_ID || "YOUR_SERVICE_ID",
  //       process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID",
  //       emailParams
  //     );

  //     // Log email send to database for tracking
  //     await addDoc(collection(db, "emailLogs"), {
  //       type: "consolidated_invoice",
  //       restaurantId: selectedRestaurant.id,
  //       restaurantName: selectedRestaurant.restaurantName,
  //       recipientEmail: email || selectedRestaurant.email,
  //       invoiceGroup: {
  //         period: selectedInvoiceGroup.period,
  //         month: selectedInvoiceGroup.month,
  //         year: selectedInvoiceGroup.year,
  //         totalAmount: selectedInvoiceGroup?.totalAmount || 0,
  //         invoiceCount: selectedInvoiceGroup.invoices.length,
  //       },
  //       sentAt: new Date().toISOString(),
  //       status: "sent",
  //       emailResult: result,
  //     });

  //     setEmailStatus({
  //       show: true,
  //       message: `Invoice successfully sent to ${email || selectedRestaurant.email}`,
  //       type: "success"
  //     });

  //     setEmailDialog({ open: false, email: "", message: "" });
  //   } catch (error) {
  //     console.error("Error sending email:", error);

  //     // Log error to database
  //     await addDoc(collection(db, "emailLogs"), {
  //       type: "consolidated_invoice_error",
  //       restaurantId: selectedRestaurant.id,
  //       error: error.message,
  //       attemptedAt: new Date().toISOString(),
  //       status: "failed",
  //     });

  //     setEmailStatus({
  //       show: true,
  //       message: `Failed to send invoice: ${error.message}`,
  //       type: "error"
  //     });
  //   } finally {
  //     setEmailSending(false);
  //   }
  // };

  // **ADD THIS FUNCTION TO YOUR COMPONENT**
  const sanitizeMessage = (message) => {
    if (!message) return '';

    // Remove or escape problematic characters
    return message
      .replace(/[&<>"'`]/g, function (match) {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
          '`': '&#96;'
        }[match];
      })
      .replace(/\r?\n/g, '<br>'); // Convert line breaks to <br> tags
  };
  const discountInfo = {
    originalAmount: selectedInvoiceGroup?.totalAmount || 0,
    discountedTotal: calculateDiscountedTotal(selectedInvoiceGroup?.totalAmount || 0),
    discountAmount: selectedInvoiceGroup?.totalAmount || 0 - calculateDiscountedTotal(selectedInvoiceGroup?.totalAmount || 0),
    discountReason,
    discountType,
    discountValue: parseFloat(discountValue) || 0,
  };

  // **GENERATE PROFESSIONAL INVOICE HTML**
  const generateInvoiceHTML = useCallback((invoiceGroup, restaurant, discountInfo = {}) => {
    const {
      originalAmount = invoiceGroup.totalAmount,
      discountedTotal = invoiceGroup.totalAmount,
      discountAmount = 0,
      discountReason = '',
      discountType = 'none',
      discountValue = 0,
    } = discountInfo;

    const invoiceNumber = `CK-${invoiceGroup.year}-${invoiceGroup.month.slice(0, 3).toUpperCase()}-${invoiceGroup.period === "1-15" ? "A" : "B"}`;
    const periodText = `${formatDate(invoiceGroup.startDate)} - ${formatDate(invoiceGroup.endDate)}`;
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });

    // Only show discount section if there is a discount
    const discountSection = discountType !== 'none' && discountAmount > 0 ? `
    <div style="margin-bottom: 15px;">
      <strong style="color: #1a4d3a;">💸 Discount:</strong> 
      <span style="color: #f39c12; font-weight: 600;">£${safeToFixed(discountAmount)}</span>
    </div>
    ${discountReason ? `
    <div style="margin-bottom: 15px;">
      <strong style="color: #1a4d3a;">📝 Reason:</strong> 
      <span style="color: #666; font-style: italic;">${discountReason}</span>
    </div>
    ` : ''}
    <div style="margin-bottom: 15px;">
      <strong style="color: #1a4d3a;">🤑 FINAL TOTAL:</strong> 
      <span style="color: #8b1538; font-size: 22px; font-weight: bold;">£${safeToFixed(discountedTotal)}</span>
    </div>
  ` : `
    <div style="margin-bottom: 15px;">
      <strong style="color: #1a4d3a;">💰 TOTAL AMOUNT:</strong> 
      <span style="color: #8b1538; font-size: 22px; font-weight: bold;">£${safeToFixed(originalAmount)}</span>
    </div>
  `;

    return `
    <div style="margin-bottom: 30px;">
      <!-- Bill From Section -->
      <div style="background: #fcffe9; padding: 20px; border-radius: 8px; border: 1px solid #e6f2e6; margin-bottom: 20px;">
        <h3 style="color: #1a4d3a; margin: 0 0 15px 0; font-size: 18px;">📍 Bill From:</h3>
        <p style="margin: 0 0 5px 0; font-weight: 600;">Crispydose Kitchen Limited</p>
        <p style="margin: 0 0 5px 0; color: #666;">20, Portman Road Reading</p>
        <p style="margin: 0 0 5px 0; color: #666;">RG30 1EA, United Kingdom</p>
        <p style="margin: 0; color: #666;">📧 Email: cdcentralkitchen@gmail.com</p>
      </div>
      
      <!-- Bill To Section -->
      <div style="background: #fcffe9; padding: 20px; border-radius: 8px; border: 1px solid #e6f2e6; margin-bottom: 20px;">
        <h3 style="color: #1a4d3a; margin: 0 0 15px 0; font-size: 18px;">📍 Bill To:</h3>
        <p style="margin: 0 0 5px 0; font-weight: 600;">${restaurant.restaurantName}</p>
        <p style="margin: 0 0 5px 0; color: #666;">📧 ${restaurant.email || 'N/A'}</p>
        <p style="margin: 0; color: #666;">📍 ${restaurant.address || 'N/A'}</p>
      </div>
    </div>

    <!-- Invoice Details -->
    <div style="background: #f7e7ce; padding: 20px; border-radius: 8px; border: 1px solid #d4af37; margin-bottom: 30px;">
      <div style="margin-bottom: 15px;">
        <strong style="color: #1a4d3a;">📄 Invoice Number:</strong> 
        <span style="color: #8b1538; font-weight: bold;">${invoiceNumber}</span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: #1a4d3a;">📅 Billing Period:</strong> 
        <span style="color: #8b1538;">${periodText}</span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: #1a4d3a;">📅 Invoice Date:</strong> 
        <span style="color: #8b1538;">${currentDate}</span>
      </div>
      <div style="margin-bottom: 15px;">
        <strong style="color: #1a4d3a;">💳 Payment Status:</strong> 
        <span style="font-weight: bold; color: ${invoiceGroup.paymentStatus === 'Paid' ? '#2e7d32' : invoiceGroup.paymentStatus === 'Overdue' ? '#d32f2f' : '#ed6c02'};">
          ${invoiceGroup.paymentStatus || 'Pending'}
        </span>
      </div>
      ${discountSection}
    </div>

    <!-- Invoice Table -->
    <table style="width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
      <thead>
        <tr style="background: linear-gradient(135deg, #1a4d3a 0%, #4CAF50 100%); color: white;">
          <th style="padding: 12px; text-align: left; border-bottom: 1px solid #ddd;">📅 Date</th>
          <th style="padding: 12px; text-align: left; border-bottom: 1px solid #ddd;">📄 Invoice #</th>
          <th style="padding: 12px; text-align: left; border-bottom: 1px solid #ddd;">📦 Items</th>
          <th style="padding: 12px; text-align: left; border-bottom: 1px solid #ddd;">💷 Amount</th>
        </tr>
      </thead>
      <tbody>
        ${invoiceGroup.invoices.map((invoice, index) => `
          <tr style="${index % 2 === 0 ? 'background-color: #f9f9f9;' : 'background-color: white;'}">
            <td style="padding: 10px; border-bottom: 1px solid #eee;">${formatDate(invoice.createdAt)}</td>
            <td style="padding: 10px; border-bottom: 1px solid #eee; font-family: monospace;">#${invoice.id.slice(0, 8)}...</td>
            <td style="padding: 10px; border-bottom: 1px solid #eee;">${invoice.items?.length || 0} items</td>
            <td style="padding: 10px; border-bottom: 1px solid #eee; font-weight: bold; color: #4CAF50;">£${safeToFixed(invoice.totalPrice)}</td>
          </tr>
        `).join('')}
      </tbody>
      <tfoot>
        <tr style="background: linear-gradient(135deg, #1a4d3a 0%, #4CAF50 100%); color: white; font-weight: bold;">
          <td style="padding: 15px;" colspan="2"><strong>📊 GRAND TOTAL</strong></td>
          <td style="padding: 15px;"><strong>${invoiceGroup.invoices.reduce((sum, inv) => sum + (inv.items?.length || 0), 0)} items</strong></td>
          <td style="padding: 15px;"><strong>£${safeToFixed(discountedTotal)}</strong></td>
        </tr>
      </tfoot>
    </table>
  `;
  }, [safeToFixed, formatDate]); // ✅ no discount state in deps






  // **UPDATED SEND EMAIL FUNCTION**
  // **CORRECT - Send to customer's email**
  const sendConsolidatedInvoice = async (customEmail, message = "") => {
    if (!selectedInvoiceGroup || !selectedRestaurant) {
      setEmailStatus({
        show: true,
        message: "No invoice selected to send",
        type: "error"
      });
      return;
    }

    // Use customer's email from restaurant data, or allow override
    const recipientEmail = customEmail || selectedRestaurant.email;

    if (!recipientEmail) {
      setEmailStatus({
        show: true,
        message: "No customer email found. Please provide an email address.",
        type: "error"
      });
      return;
    }

    setEmailSending(true);

    try {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

      const invoiceNumber = `CK-${selectedInvoiceGroup.year}-${selectedInvoiceGroup.month.slice(0, 3).toUpperCase()}-${selectedInvoiceGroup.period === "1-15" ? "A" : "B"}`;
      const invoiceHtmlContent = generateInvoiceHTML(selectedInvoiceGroup, selectedRestaurant, discountInfo);


      const emailParams = {
        to_email: recipientEmail, // Customer's email
        to_name: selectedRestaurant.restaurantName,
        reply_to_email: 'cdcentralkitchen@gmail.com', // Your email for replies
        invoice_number: invoiceNumber,
        custom_message: sanitizeMessage(message),
        invoice_html_content: invoiceHtmlContent,
        current_year: new Date().getFullYear(),
        total_amount: safeToFixed(groupDiscountedTotal, 0),
        billing_period: `${formatDate(selectedInvoiceGroup.startDate)} - ${formatDate(selectedInvoiceGroup.endDate)}`,
        restaurant_name: selectedRestaurant.restaurantName,
      };

      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        emailParams
      );

      // Log success
      await addDoc(collection(db, "emailLogs"), {
        type: "consolidated_invoice",
        restaurantId: selectedRestaurant.id,
        restaurantName: selectedRestaurant.restaurantName,
        recipientEmail: recipientEmail,
        invoiceNumber: invoiceNumber,
        totalAmount: selectedInvoiceGroup?.totalAmount || 0,
        sentAt: new Date().toISOString(),
        status: "sent"
      });

      setEmailStatus({
        show: true,
        message: `📧 Invoice successfully sent to ${recipientEmail}!`,
        type: "success"
      });

      setEmailDialog({ open: false, email: "", message: "" });

    } catch (error) {
      console.error("Error sending email:", error);
      setEmailStatus({
        show: true,
        message: `❌ Failed to send invoice: ${error.text || error.message}`,
        type: "error"
      });
    } finally {
      setEmailSending(false);
    }
  };

  console.log(selectedRestaurant);


  // **EXISTING FUNCTIONS** (keeping all your existing logic)
  const togglePeriodExpansion = (periodKey) => {
    setExpandedPeriods((prev) => ({
      ...prev,
      [periodKey]: !prev[periodKey],
    }));
  };

  const filteredInvoices = consolidatedInvoices.filter((group) => {
    if (activeFilter === "all") return true;
    return String(group.paymentStatus || '').toLowerCase() === activeFilter.toLowerCase();
  });

  const fetchRestaurants = async () => {
    try {
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      const restaurantData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRestaurants(restaurantData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setLoading(false);
    }
  };

  const fetchInvoices = (restaurantId) => {
    console.log("🔍 Fetching invoices for restaurant ID:", restaurantId);

    const invoicesRef = collection(db, "invoices");

    const unsubscribe = onSnapshot(
      invoicesRef,
      (querySnapshot) => {
        console.log("📋 TOTAL docs in invoices collection:", querySnapshot.docs.length);

        // Convert all documents to invoice objects
        const allInvoices = querySnapshot.docs.map(doc => {
          const data = doc.data();

          // **IMPORTANT: Convert string dates to Date objects**
          if (typeof data.createdAt === 'string') {
            console.log("🔧 Converting string date to Date object:", data.createdAt);
            data.createdAt = new Date(data.createdAt);
          }

          return {
            id: doc.id,
            ...data
          };
        });

        // Filter invoices for this restaurant
        const matchingInvoices = allInvoices.filter(invoice =>
          invoice.userId === restaurantId
        );

        console.log("🎯 Matching invoices:", matchingInvoices.length);
        console.log("📊 Matching invoices data:", matchingInvoices);

        if (matchingInvoices.length === 0) {
          console.log("⚠️ No matching invoices found");
          setConsolidatedInvoices([]);
          setLoading(false);
          return;
        }

        // **THIS IS THE KEY PART YOU WERE MISSING**
        // Group the invoices by period
        const restaurantInvoices = matchingInvoices;

        const groups = buildConsolidatedInvoices(
          restaurantInvoices,
          periodType,
          fromDate,
          toDate
        );
        setConsolidatedInvoices(groups);

        // Keep selectedInvoiceGroup in sync if modal is currently open
        setSelectedInvoiceGroup((prev) => {
          if (!prev) return null;
          const currentKey = `${prev.year}-${prev.month}-${prev.period}`;
          const updated = groups.find(
            (g) => `${g.year}-${g.month}-${g.period}` === currentKey
          );
          if (updated) {
            setPaymentStatus(updated.paymentStatus);
            return updated;
          }
          return prev;
        });

        // ✅ ALSO PREPARE ROW DISCOUNTS CACHE
        const rowCache = {};
        restaurantInvoices.forEach(inv => {
          if (inv.discountType) {
            rowCache[inv.id] = {
              type: inv.discountType,
              value: inv.discountValue ?? '',
              reason: inv.discountReason ?? '',
            };
          }
        });
        setRowDiscounts(rowCache);

        setLoading(false);

      },
      (error) => {
        console.error("❌ Error in onSnapshot:", error);
        setLoading(false);
      }
    );

    return unsubscribe;
  };



  // const groupInvoicesByPeriod = (invoices) => {
  //   console.log("Grouping invoices by WEEK, count:", invoices.length);
  //   const grouped = {};

  //   invoices.forEach((invoice, index) => {
  //     // Handle different date formats
  //     let date;
  //     if (typeof invoice.createdAt === "string") {
  //       date = new Date(invoice.createdAt);
  //     } else if (invoice.createdAt?.toDate) {
  //       date = invoice.createdAt.toDate();
  //     } else if (invoice.createdAt instanceof Date) {
  //       date = invoice.createdAt;
  //     } else {
  //       console.warn("Invalid createdAt format for invoice", invoice.id);
  //       return;
  //     }

  //     // **🔥 NEW: CALCULATE WEEK BOUNDARIES (SUNDAY TO SATURDAY)**
  //     const getWeekBoundaries = (inputDate) => {
  //       const d = new Date(inputDate);
  //       const day = d.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  //       // Calculate Sunday (start of week)
  //       const startDate = new Date(d);
  //       startDate.setDate(d.getDate() - day);
  //       startDate.setHours(0, 0, 0, 0);

  //       // Calculate Saturday (end of week)  
  //       const endDate = new Date(startDate);
  //       endDate.setDate(startDate.getDate() + 6);
  //       endDate.setHours(23, 59, 59, 999);

  //       return { startDate, endDate };
  //     };

  //     const { startDate, endDate } = getWeekBoundaries(date);

  //     // **🔥 NEW: CREATE UNIQUE KEY FOR EACH WEEK**
  //     const year = startDate.getFullYear();
  //     const month = startDate.getMonth();
  //     const weekNumber = Math.ceil(startDate.getDate() / 7);
  //     const key = `${year}-${month}-week-${weekNumber}`;

  //     console.log(`Grouping invoice ${invoice.id} into week:`, {
  //       key,
  //       startDate: startDate.toISOString().slice(0, 10),
  //       endDate: endDate.toISOString().slice(0, 10),
  //       invoiceDate: date.toISOString().slice(0, 10)
  //     });

  //     if (!grouped[key]) {
  //       grouped[key] = {
  //         period: `Week ${weekNumber}`, // **🔥 NEW: Week display**
  //         month: startDate.toLocaleString("default", { month: "long" }),
  //         year,
  //         weekNumber,
  //         invoices: [],
  //         totalAmount: 0,
  //         totalItems: 0,
  //         startDate, // **🔥 SUNDAY**
  //         endDate,   // **🔥 SATURDAY**
  //         isPaid: true,
  //       };
  //     }

  //     grouped[key].invoices.push(invoice);
  //     const invoiceTotalPrice = parseFloat(invoice.totalPrice) || 0;
  //     grouped[key].totalAmount += invoiceTotalPrice;
  //     grouped[key].totalItems += invoice.items ? invoice.items.length : 0;

  //     if (!invoice.isBillPaid) {
  //       grouped[key].isPaid = false;
  //     }
  //   });

  //   const result = Object.values(grouped)
  //     .map((group) => ({
  //       ...group,
  //       paymentStatus: group.isPaid ? "Paid" : "Pending",
  //     }))
  //     .sort((a, b) => b.endDate - a.endDate); // **🔥 Sort by end date (latest first)**

  //   console.log("Final weekly grouped result:", result);
  //   return result;
  // };

  const buildConsolidatedInvoices = (invoices, periodType, fromDate, toDate) => {
    const filtered = filterInvoicesByPeriod(invoices, periodType, fromDate, toDate);

    const grouped = {};

    filtered.forEach(inv => {
      const ts = inv.createdAt;
      const date = ts?.toDate ? ts.toDate() : new Date(ts);
      const monthName = moment(date).format('MMMM');
      const year = date.getFullYear();
      const userId = inv.userId;

      let periodKey;
      let periodLabel;
      let startOfPeriod;
      let endOfPeriod;

      if (periodType === 'week') {
        // Sunday → Saturday
        startOfPeriod = moment(date).startOf('week').toDate();
        endOfPeriod = moment(date).endOf('week').toDate();

        periodKey = moment(startOfPeriod).format('YYYY-MM-DD');
        periodLabel = `${moment(startOfPeriod).format('D MMM')} – ${moment(endOfPeriod).format('D MMM')}`;
      }

      else if (periodType === 'month') {
        startOfPeriod = moment(date).startOf('month').toDate();
        endOfPeriod = moment(date).endOf('month').toDate();

        periodKey = `${year}-${monthName}`;
        periodLabel = `${monthName} ${year}`;
      }

      else if (periodType === 'custom' && fromDate && toDate) {
        startOfPeriod = moment(fromDate).startOf('day').toDate();
        endOfPeriod = moment(toDate).endOf('day').toDate();

        periodKey = `${moment(fromDate).format('YYYY-MM-DD')}_${moment(toDate).format('YYYY-MM-DD')}`;
        periodLabel = `${moment(fromDate).format('D MMM YYYY')} - ${moment(toDate).format('D MMM YYYY')}`;
      }


      const key = `${userId}-${periodKey}`;

      if (!grouped[key]) {
        grouped[key] = {
          userId,
          year,
          month: monthName,
          period: periodKey,
          periodLabel,            // for UI
          invoices: [],
          totalAmount: 0,
          totalTax: 0,
          totalSubtotal: 0,
          startDate: startOfPeriod,        // will be adjusted below
          endDate: endOfPeriod,          // will be adjusted below
          paymentStatus: 'Pending',
          totalOriginalAmount: 0,
          hasDiscounts: false,
        };
      }

      const g = grouped[key];
      const effectiveTotal =
        inv.discountedTotal !== undefined && inv.discountedTotal !== null
          ? safeNumber(inv.discountedTotal)
          : safeNumber(inv.totalPrice)
      g.invoices.push(inv);
      g.totalAmount += effectiveTotal
      g.totalTax += safeNumber(inv.tax);
      g.totalSubtotal += safeNumber(inv.subtotal);

      if (date < g.startDate) g.startDate = date;
      if (date > g.endDate) g.endDate = date;

    });

    const result = Object.values(grouped).map(g => {
      const isInvPaid = (inv) =>
        inv.isBillPaid === true ||
        inv.isBillPaid === 'true' ||
        String(inv.paymentStatus || '').toLowerCase() === 'paid' ||
        String(inv.orderStatus || '').toLowerCase() === 'paid';

      const isInvOverdue = (inv) =>
        String(inv.paymentStatus || '').toLowerCase() === 'overdue';

      let paymentStatus = 'Pending';
      if (g.invoices.length > 0 && g.invoices.every(isInvPaid)) {
        paymentStatus = 'Paid';
      } else if (g.invoices.some(isInvOverdue)) {
        paymentStatus = 'Overdue';
      }

      return {
        ...g,
        paymentStatus,
        isPaid: paymentStatus === 'Paid',
        // periodText used by generateInvoiceHTML
        periodText: `${moment(g.startDate).format('MMM D, YYYY')} - ${moment(g.endDate).format('MMM D, YYYY')}`,
      };
    });

    // Sort by startDate
    result.sort((a, b) => b.endDate - a.endDate);

    return result;
  };




  const handleGroupStatusChange = async (group, newStatus) => {
    if (!group || !group.invoices) return;
    const isPaid = newStatus === "Paid";

    try {
      await Promise.all(
        group.invoices.map((invoice) => {
          const invoiceDocRef = doc(db, "invoices", invoice.id);
          return updateDoc(invoiceDocRef, {
            isBillPaid: isPaid,
            paymentStatus: newStatus,
          });
        })
      );

      // Update consolidatedInvoices immediately
      setConsolidatedInvoices((prev) =>
        prev.map((g) => {
          if (
            g.period === group.period &&
            g.year === group.year &&
            g.month === group.month
          ) {
            return {
              ...g,
              invoices: g.invoices.map((inv) => ({
                ...inv,
                isBillPaid: isPaid,
                paymentStatus: newStatus,
              })),
              paymentStatus: newStatus,
              isPaid: isPaid,
            };
          }
          return g;
        })
      );

      // Update selectedInvoiceGroup if open
      setSelectedInvoiceGroup((prev) => {
        if (
          prev &&
          prev.period === group.period &&
          prev.year === group.year &&
          prev.month === group.month
        ) {
          return {
            ...prev,
            invoices: prev.invoices.map((inv) => ({
              ...inv,
              isBillPaid: isPaid,
              paymentStatus: newStatus,
            })),
            paymentStatus: newStatus,
            isPaid: isPaid,
          };
        }
        return prev;
      });

      setPaymentStatus(newStatus);

      setEmailStatus({
        show: true,
        message: `Payment status updated to ${newStatus}!`,
        type: "success",
      });
    } catch (error) {
      console.error("Error updating payment status: ", error);
      setEmailStatus({
        show: true,
        message: "Failed to update payment status.",
        type: "error",
      });
    }
  };

  const handlePaymentStatusChange = async (newStatus) => {
    if (!selectedInvoiceGroup) return;
    await handleGroupStatusChange(selectedInvoiceGroup, newStatus);
  };

  const handleDownloadPDF = async () => {
    setIsPrinting(true);
    try {
      // Allow React to re-render with isPrinting=true so status badge is rendered cleanly
      await new Promise((resolve) => setTimeout(resolve, 300));

      const element = invoiceRef.current;
      if (!element) {
        console.error("Element not found for PDF generation");
        return;
      }

      // Create a clone for manipulation
      const clone = element.cloneNode(true);

      // Remove all elements with class 'hide-in-pdf'
      const hideElements = clone.querySelectorAll('.hide-in-pdf');
      hideElements.forEach((el) => {
        if (el.tagName === 'TD' || el.tagName === 'TH') {
          el.style.display = 'none';
        } else {
          el.parentNode?.removeChild(el);
        }
      });

      // Also hide the entire "Action" column header
      const tableHeaders = clone.querySelectorAll('thead th');
      tableHeaders.forEach((th, index) => {
        if (th.textContent.includes('Action') || th.querySelector('.hide-in-pdf')) {
          th.style.display = 'none';

          // Also hide corresponding cells in tbody
          const rows = clone.querySelectorAll('tbody tr');
          rows.forEach((row) => {
            const cells = row.querySelectorAll('td');
            if (cells[index]) {
              cells[index].style.display = 'none';
            }
          });
        }
      });

      // Create print-specific CSS
      const style = document.createElement('style');
      style.innerHTML = `
        .hide-in-pdf, 
        .hide-in-pdf td, 
        .hide-in-pdf th,
        td.hide-in-pdf, 
        th.hide-in-pdf {
          display: none !important;
          visibility: hidden !important;
        }
        
        @media print {
          .hide-in-pdf, 
          .hide-in-pdf td, 
          .hide-in-pdf th,
          td.hide-in-pdf, 
          th.hide-in-pdf,
          .no-print {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `;
      document.head.appendChild(style);

      const options = {
        margin: [15, 15, 15, 15],
        filename: `consolidated-invoice-${selectedRestaurant?.restaurantName || 'details'}-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          windowWidth: 1200,
          windowHeight: 1600,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'landscape',
        },
      };

      await html2pdf().set(options).from(clone).save();

      // Clean up
      document.head.removeChild(style);
    } catch (error) {
      console.error("PDF generation error:", error);
    } finally {
      setIsPrinting(false);
    }
  };

  // **EFFECTS**
  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      setLoading(true);
      const unsubscribe = fetchInvoices(selectedRestaurant.id);
      return () => unsubscribe();
    }
  }, [selectedRestaurant]);

  useEffect(() => {
    if (!selectedRestaurant) return;
    fetchInvoices(selectedRestaurant.id);
  }, [selectedRestaurant, periodType, fromDate, toDate]);

  useEffect(() => {
    // ✅ Safe check: only run if group exists AND has totalAmount
    if (selectedInvoiceGroup?.totalAmount) {
      setOriginalAmount(selectedInvoiceGroup?.totalAmount || 0);
    }
  }, [selectedInvoiceGroup?.totalAmount]);

  // **FILTERED DATA**
  const filteredRestaurants = restaurants.filter((restaurant) =>
    restaurant.restaurantName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvoiceGroup(null);
  };

  const handleBackToRestaurants = () => {
    setSelectedRestaurant(null);
    setConsolidatedInvoices([]);
    setSelectedInvoiceGroup(null);
    setShowModal(false);
  };

  const groupDiscountedTotal = useMemo(() => {
    return selectedInvoiceGroup?.invoices.reduce((sum, inv) =>
      sum + (inv.discountedTotal || inv.totalPrice || 0), 0
    ) || 0
  }, [selectedInvoiceGroup?.invoices])

  const truncateText = (text = '', length = 20) => {
    if (!text) return '';
    return text.length > length ? text.slice(0, length) + '…' : text;
  };

  const groupTotals = useMemo(() => {
    if (!selectedInvoiceGroup) {
      return { items: 0, original: 0, discounted: 0 };
    }

    let totalItems = 0;
    let originalTotal = 0;
    let discountedTotal = 0;

    selectedInvoiceGroup.invoices.forEach(inv => {
      totalItems += inv.items?.length || 0;
      originalTotal += inv.totalPrice || 0;
      discountedTotal += inv.discountedTotal ?? inv.totalPrice ?? 0;
    });

    return {
      items: totalItems,
      original: originalTotal,
      discounted: discountedTotal,
    };
  }, [selectedInvoiceGroup?.invoices]);


  return (
    <PageContainer>
      {/* <Header /> */}

      {/* **SUCCESS/ERROR SNACKBAR** */}
      <Snackbar
        open={emailStatus.show}
        autoHideDuration={6000}
        onClose={() => setEmailStatus({ ...emailStatus, show: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setEmailStatus({ ...emailStatus, show: false })}
          severity={emailStatus.type}
          sx={{ borderRadius: appTheme.borderRadius.medium }}
        >
          {emailStatus.message}
        </Alert>
      </Snackbar>

      {/* **EMAIL SEND DIALOG** */}
      <Dialog
        open={emailDialog.open}
        onClose={() => !emailSending && setEmailDialog({ open: false, email: "", message: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: appTheme.colors.primary, fontWeight: 'bold' }}>
          <Send /> Send Invoice to Customer
        </DialogTitle>

        <DialogContent>
          <Box sx={{ marginTop: 2 }}>
            <TextField
              fullWidth
              label="Customer Email"
              value={emailDialog.email}
              onChange={(e) => setEmailDialog(prev => ({ ...prev, email: e.target.value }))}
              placeholder={selectedRestaurant?.email || "Enter customer email"}
              helperText={`Default: ${selectedRestaurant?.email || 'No email found'}`}
              margin="normal"
            />

            {/* <TextField
              fullWidth
              label="Custom Message (Optional)"
              value={emailDialog.message}
              onChange={(e) => setEmailDialog(prev => ({ ...prev, message: e.target.value }))}
              multiline
              rows={3}
              placeholder="Add a personal message for the customer..."
              margin="normal"
            /> */}
          </Box>
        </DialogContent>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', padding: 2 }}>
          <ActionButton onClick={() => setEmailDialog({ open: false, email: "", message: "" })}>
            Cancel
          </ActionButton>

          <ActionButton
            onClick={() => sendConsolidatedInvoice(emailDialog.email, emailDialog.message)}
            disabled={emailSending}
            variant="contained"
            sx={{
              backgroundColor: appTheme.colors.primary,
              '&:hover': { backgroundColor: appTheme.colors.deepTeal }
            }}
          >
            {emailSending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Sending...
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Send size={16} />
                Send to Customer
              </Box>
            )}
          </ActionButton>
        </Box>
      </Dialog>



      {/* **MAIN CONTENT** */}
      {!selectedRestaurant ? (
        // **RESTAURANT SELECTION VIEW**
        <Fade in={!selectedRestaurant}>
          <div>
            <HeaderCard sx={{ padding: appTheme.spacing.xl }}>
              <Typography variant="h3" sx={{
                fontWeight: appTheme.typography.fontWeights.black,
                marginBottom: appTheme.spacing.sm,
              }}>
                🏢 Consolidated Invoice Management
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Select a restaurant to view and manage consolidated invoices
              </Typography>
            </HeaderCard>

            <StyledCard sx={{ marginBottom: appTheme.spacing.xl }}>
              <CardContent sx={{ padding: appTheme.spacing.lg }}>
                <Box sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', md: 'nowrap' } // Wrap on mobile devices
                }}>
                  {/* Search field - takes up more space */}
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <SearchField
                      fullWidth
                      placeholder="Search restaurants by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: appTheme.colors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Filter area */}
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    {/* Period type select */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="period-type-label">Period</InputLabel>
                      <Select
                        labelId="period-type-label"
                        value={periodType}
                        label="Period"
                        onChange={(e) => setPeriodType(e.target.value)}
                      >
                        <MenuItem value="week">Weekly (Sun-Sat)</MenuItem>
                        <MenuItem value="month">Full Month</MenuItem>
                        <MenuItem value="custom">Custom Range</MenuItem>
                      </Select>
                    </FormControl>

                    {periodType === 'custom' && (
                      <>
                        <TextField
                          label="From date"
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={fromDate ? moment(fromDate).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setFromDate(e.target.value ? new Date(e.target.value) : null)}
                        />
                        <TextField
                          label="To date"
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={toDate ? moment(toDate).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setToDate(e.target.value ? new Date(e.target.value) : null)}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>

            <StyledTableContainer>
              <Table>
                <StyledTableHead>
                  <TableRow>
                    <TableCell>Restaurant</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {filteredRestaurants.map((restaurant) => (
                    <TableRow
                      key={restaurant.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: alpha(appTheme.colors.primary, 0.05),
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle1" sx={{
                            fontWeight: appTheme.typography.fontWeights.semibold,
                            color: appTheme.colors.textPrimary,
                          }}>
                            {restaurant.restaurantName}
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: appTheme.colors.textSecondary,
                          }}>
                            ID: {restaurant.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {restaurant.address || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" sx={{ color: appTheme.colors.textSecondary }} />
                            {restaurant.phone || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Mail size={16} color={appTheme.colors.textSecondary} />
                            {restaurant.email || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <ActionButton
                          variant="contained"
                          onClick={() => {
                            setSelectedRestaurant(restaurant);
                            fetchInvoices(restaurant.id);
                          }}
                          sx={{
                            backgroundColor: appTheme.colors.primary,
                            '&:hover': {
                              backgroundColor: appTheme.colors.deepTeal,
                            },
                          }}
                        >
                          View Invoices
                        </ActionButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </div>
        </Fade>
      ) : (
        // **INVOICE PERIODS VIEW**
        <Fade in={!!selectedRestaurant}>
          <div>
            <HeaderCard sx={{ padding: appTheme.spacing.lg }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={handleBackToRestaurants}
                    sx={{
                      color: 'inherit',
                      backgroundColor: alpha('#FFFFFF', 0.1),
                      '&:hover': {
                        backgroundColor: alpha('#FFFFFF', 0.2),
                      },
                    }}
                  >
                    <ArrowLeft />
                  </IconButton>
                  <Box>
                    <Typography variant="h4" sx={{
                      fontWeight: appTheme.typography.fontWeights.bold,
                      marginBottom: appTheme.spacing.xs,
                    }}>
                      📊 {selectedRestaurant.restaurantName}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Consolidated invoice periods and billing history
                    </Typography>
                  </Box>
                </Box>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: 'inherit' }}>Filter</InputLabel>
                  <Select
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                    label="Filter"
                    sx={{
                      color: 'inherit',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.3),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.5),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.7),
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </HeaderCard>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: appTheme.spacing.huge }}>
                <CircularProgress size={50} sx={{ color: appTheme.colors.primary }} />
              </Box>
            ) : (
              <StyledTableContainer>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Date Range</TableCell>
                      <TableCell>Invoices</TableCell>
                      {/* <TableCell>Items</TableCell> */}
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {filteredInvoices.map((group) => {
                      const periodKey = `${group.year}-${group.month}-${group.period}`;
                      const isExpanded = expandedPeriods[periodKey];

                      return (
                        <React.Fragment key={periodKey}>
                          <TableRow
                            sx={{
                              "&:hover": {
                                backgroundColor: alpha(appTheme.colors.primary, 0.05),
                              }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="subtitle1" sx={{
                                  fontWeight: appTheme.typography.fontWeights.semibold,
                                }}>
                                  {group.month} {group.year}
                                </Typography>
                                <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                                  {group.period} ({formatDate(group.startDate)} - {formatDate(group.endDate)})
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(group.startDate)} - {formatDate(group.endDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={group.invoices.length}
                                sx={{
                                  backgroundColor: alpha(appTheme.colors.primary, 0.1),
                                  color: appTheme.colors.primary,
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            {/* <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {group.totalItems}
                              </Typography>
                            </TableCell> */}
                            <TableCell>
                              <Typography variant="subtitle1" sx={{
                                fontWeight: appTheme.typography.fontWeights.bold,
                                color: appTheme.colors.success,
                              }}>
                                £{safeToFixed(group.totalAmount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <Select
                                  value={group.paymentStatus || 'Pending'}
                                  onChange={(e) => handleGroupStatusChange(group, e.target.value)}
                                  size="small"
                                  sx={{
                                    height: 32,
                                    fontSize: '0.8125rem',
                                    fontWeight: 600,
                                    borderRadius: appTheme.borderRadius.soft,
                                    backgroundColor:
                                      group.paymentStatus === 'Paid'
                                        ? alpha(appTheme.colors.success, 0.1)
                                        : group.paymentStatus === 'Overdue'
                                        ? alpha(appTheme.colors.error, 0.1)
                                        : alpha(appTheme.colors.warning, 0.1),
                                    color:
                                      group.paymentStatus === 'Paid'
                                        ? appTheme.colors.success
                                        : group.paymentStatus === 'Overdue'
                                        ? appTheme.colors.error
                                        : appTheme.colors.warning,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      border: `1px solid ${
                                        group.paymentStatus === 'Paid'
                                          ? alpha(appTheme.colors.success, 0.3)
                                          : group.paymentStatus === 'Overdue'
                                          ? alpha(appTheme.colors.error, 0.3)
                                          : alpha(appTheme.colors.warning, 0.3)
                                      }`,
                                    },
                                    '& .MuiSelect-select': {
                                      paddingY: '4px',
                                      paddingX: '10px',
                                    },
                                  }}
                                >
                                  <MenuItem value="Pending">Pending</MenuItem>
                                  <MenuItem value="Paid">Paid</MenuItem>
                                  <MenuItem value="Overdue">Overdue</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    onClick={() => {
                                      setSelectedInvoiceGroup(group);

                                      // ✅ HYDRATE ROW DISCOUNTS FROM FIRESTORE DATA
                                      const hydrated = {};
                                      group.invoices.forEach(inv => {
                                        if (inv.discountType) {
                                          hydrated[inv.id] = {
                                            type: inv.discountType || 'none',
                                            value: inv.discountValue ?? '',
                                            reason: inv.discountReason ?? '',
                                          };
                                        }
                                      });
                                      setRowDiscounts(hydrated);

                                      setPaymentStatus(group.paymentStatus);
                                      setShowModal(true);
                                    }}

                                  >
                                    <Eye size={18} />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Toggle Details">
                                  <IconButton
                                    onClick={() => togglePeriodExpansion(periodKey)}
                                    sx={{
                                      color: appTheme.colors.secondary,
                                      backgroundColor: alpha(appTheme.colors.secondary, 0.1),
                                      '&:hover': {
                                        backgroundColor: alpha(appTheme.colors.secondary, 0.2),
                                      },
                                    }}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* **EXPANDED DETAILS** */}
                          <TableRow>
                            <TableCell colSpan={7} sx={{ padding: 0, border: 'none' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{
                                  margin: appTheme.spacing.md,
                                  padding: appTheme.spacing.lg,
                                  backgroundColor: alpha(appTheme.colors.jadeFog, 0.3),
                                  borderRadius: appTheme.borderRadius.medium,
                                  border: `1px solid ${appTheme.colors.borderPrimary}`,
                                }}>
                                  <Typography variant="h6" sx={{
                                    marginBottom: appTheme.spacing.md,
                                    color: appTheme.colors.primary,
                                    fontWeight: appTheme.typography.fontWeights.bold,
                                  }}>
                                    📋 Individual Invoices
                                  </Typography>

                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {group.invoices.map((invoice) => (
                                        <TableRow
                                          key={invoice.id}
                                          sx={{
                                            "&:hover": {
                                              backgroundColor: alpha(appTheme.colors.primary, 0.05),
                                            }
                                          }}
                                        >
                                          <TableCell>
                                            <Typography variant="body2" sx={{
                                              fontFamily: 'monospace',
                                              color: appTheme.colors.primary,
                                              fontWeight: 600,
                                            }}>
                                              #{invoice.id.slice(0, 8)}...
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {formatDate(invoice.createdAt)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {invoice.items?.length || 0}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{
                                              fontWeight: 600,
                                              color: appTheme.colors.success,
                                            }}>
                                              £{safeToFixed(invoice.totalPrice)}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            )}
          </div>
        </Fade>
      )}

      {/* **INVOICE DETAILS MODAL** */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: appTheme.borderRadius.rounded,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          background: appTheme.gradients.hero,
          color: appTheme.colors.champagne,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: appTheme.spacing.lg,
        }}>
          <Typography variant="h5" sx={{
            fontWeight: appTheme.typography.fontWeights.bold,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            Consolidated Invoice
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: 'inherit',
              backgroundColor: alpha('#FFFFFF', 0.1),
              '&:hover': {
                backgroundColor: alpha('#FFFFFF', 0.2),
              },
            }}
          >
            <X />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          {selectedInvoiceGroup && (
            <Box ref={invoiceRef} sx={{ padding: appTheme.spacing.xl }}>
              {/* **BILL FROM/TO SECTION** */}
              <Grid container spacing={3} sx={{ marginBottom: appTheme.spacing.xl }}>
                <Grid item xs={12} md={6}>
                  <StyledCard sx={{
                    background: appTheme.gradients.cardLuxury,
                    border: `2px solid ${appTheme.colors.primary}`,
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{
                        color: appTheme.colors.primary,
                        fontWeight: appTheme.typography.fontWeights.bold,
                        marginBottom: appTheme.spacing.md,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <Building2 size={20} /> Bill From:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 1 }}>
                        Crispydosa Kitchen Limited
                      </Typography>
                      <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                        20, Portman Road Reading, RG30 1Ea, United Kingdom
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledCard sx={{
                    background: appTheme.gradients.cardPremium,
                    border: `2px solid ${appTheme.colors.secondary}`,
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{
                        color: appTheme.colors.secondary,
                        fontWeight: appTheme.typography.fontWeights.bold,
                        marginBottom: appTheme.spacing.md,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <Business size={20} /> Bill To:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 1 }}>
                        {selectedRestaurant.restaurantName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary, marginBottom: 0.5 }}>
                        📞 {selectedRestaurant.phone || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary, marginBottom: 0.5 }}>
                        📧 {selectedRestaurant.email || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: appTheme.colors.textSecondary }}>
                        📍 {selectedRestaurant.address || 'N/A'}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>
              </Grid>

              {/* **INVOICE DETAILS** */}
              <StyledCard sx={{
                marginBottom: appTheme.spacing.xl,
                background: `linear-gradient(135deg, ${appTheme.colors.goldenHour} 0%, ${appTheme.colors.ivoryMist} 100%)`,
                border: `2px solid ${appTheme.colors.accent}`,
              }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Invoice Period:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedInvoiceGroup.startDate)} - {formatDate(selectedInvoiceGroup.endDate)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Invoice Number:
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          CDK-${selectedInvoiceGroup.year}-${selectedInvoiceGroup.month.slice(0, 3).toUpperCase()}-W${selectedInvoiceGroup.weekNumber}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Total Amount:
                        </Typography>
                        <Typography variant="h6" sx={{
                          color: appTheme.colors.success,
                          fontWeight: appTheme.typography.fontWeights.bold,
                        }}>
                          £{safeToFixed(groupTotals.discounted, 2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Payment Status:
                        </Typography>
                        {isPrinting ? (
                          <Box
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              padding: '6px 16px',
                              borderRadius: '20px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              color:
                                paymentStatus === 'Paid'
                                  ? '#155724'
                                  : paymentStatus === 'Overdue'
                                  ? '#721c24'
                                  : '#856404',
                              backgroundColor:
                                paymentStatus === 'Paid'
                                  ? '#d4edda'
                                  : paymentStatus === 'Overdue'
                                  ? '#f8d7da'
                                  : '#fff3cd',
                              border: `2px solid ${
                                paymentStatus === 'Paid'
                                  ? '#c3e6cb'
                                  : paymentStatus === 'Overdue'
                                  ? '#f5c6cb'
                                  : '#ffeaa7'
                              }`,
                            }}
                          >
                            {paymentStatus === 'Paid' ? 'Paid' : paymentStatus === 'Overdue' ? 'Overdue' : 'Pending'}
                          </Box>
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={paymentStatus}
                              onChange={(e) => handlePaymentStatusChange(e.target.value)}
                              sx={{
                                borderRadius: appTheme.borderRadius.medium,
                              }}
                            >
                              <MenuItem value="Pending">Pending</MenuItem>
                              <MenuItem value="Paid">Paid</MenuItem>
                              <MenuItem value="Overdue">Overdue</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Box>


                    </Grid>

                  </Grid>
                </CardContent>
              </StyledCard>

              {/* **INVOICE TABLE** */}
              <StyledTableContainer>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell>Disc.Type</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Original</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Discounted</TableCell>
                      <TableCell className="hide-in-pdf" >Action</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {[...selectedInvoiceGroup.invoices]
                      .sort((a, b) => {
                        const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                        const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                        return da - db; // latest first
                      }).map((invoice) => {
                        // ✅ GET ROW DISCOUNT FROM GLOBAL STATE
                        const rowDisc = rowDiscounts[invoice.id] || {
                          type: 'none',
                          value: 0,
                          reason: ''
                        };


                        const rowDiscountedTotal =
                          rowDisc.type === 'none'
                            ? invoice.totalPrice || 0
                            : calculateDiscountedTotal(
                              invoice.totalPrice || 0,
                              rowDisc.type,
                              rowDisc.value
                            )


                        const updateRowDiscount = (field, value) => {
                          setRowDiscounts(prev => ({
                            ...prev,
                            [invoice.id]: { ...(prev[invoice.id] || {}), [field]: value }
                          }))
                        }

                        const saveRowDiscount = async () => {
                          try {
                            setRowSaveStatus(prev => ({ ...prev, [invoice.id]: 'saving' }));

                            const invoiceDocRef = doc(db, 'invoices', invoice.id);
                            await updateDoc(invoiceDocRef, {
                              discountType: rowDisc.type === 'none' ? null : rowDisc.type,
                              discountValue: rowDisc.type === 'none' ? null : parseFloat(rowDisc.value) || 0,
                              discountReason: rowDisc.type === 'none' ? null : rowDisc.reason,
                              discountedTotal: rowDiscountedTotal
                            });

                            setRowSaveStatus(prev => ({ ...prev, [invoice.id]: 'saved' }));
                            setSelectedInvoiceGroup(prev => ({
                              ...prev,
                              invoices: prev.invoices.map(inv =>
                                inv.id === invoice.id
                                  ? {
                                    ...inv,
                                    discountType: rowDisc.type === 'none' ? null : rowDisc.type,
                                    discountValue: rowDisc.type === 'none' ? null : parseFloat(rowDisc.value) || 0,
                                    discountReason: rowDisc.type === 'none' ? null : rowDisc.reason,
                                    discountedTotal: rowDiscountedTotal,
                                  }
                                  : inv
                              )
                            }));

                            // Auto-clear success state after 2 seconds
                            setTimeout(() => {
                              setRowSaveStatus(prev => {
                                const copy = { ...prev };
                                delete copy[invoice.id];
                                return copy;
                              });
                            }, 2000);

                          } catch (err) {
                            console.error('Row discount save failed', err);
                            setRowSaveStatus(prev => ({ ...prev, [invoice.id]: 'error' }));
                          }
                        };



                        return (
                          <TableRow key={invoice.id} hover>
                            <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                            <TableCell>{invoice.id.slice(0, 8)}...</TableCell>
                            <TableCell>{invoice.items?.length || 0}</TableCell>

                            {/* ✅ DISCOUNT TYPE SELECT */}
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                  value={rowDisc.type}
                                  onChange={(e) => updateRowDiscount('type', e.target.value)}
                                >
                                  <MenuItem value="none">None</MenuItem>
                                  <MenuItem value="percent">%</MenuItem>
                                  <MenuItem value="amount">£</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>

                            {/* ✅ DISCOUNT VALUE */}
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={rowDisc.type === 'none' ? 0 : rowDisc.value}
                                onChange={(e) => updateRowDiscount('value', e.target.value || 0)}
                                disabled={rowDisc.type === 'none'}
                                sx={{ width: 70 }}
                              />
                            </TableCell>

                            {/* ✅ DISCOUNT REASON */}
                            <TableCell>
                              {editingReason[invoice.id] ? (
                                <TextField
                                  size="small"
                                  value={rowDisc.reason}
                                  onChange={(e) => updateRowDiscount('reason', e.target.value)}
                                  onBlur={() =>
                                    setEditingReason(prev => ({ ...prev, [invoice.id]: false }))
                                  }
                                  autoFocus
                                  sx={{ width: 120 }}
                                />
                              ) : rowDisc.reason ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() =>
                                      setEditingReason(prev => ({ ...prev, [invoice.id]: true }))
                                    }
                                  >
                                    {truncateText(rowDisc.reason, 16)}
                                  </Typography>

                                  <Tooltip title={rowDisc.reason}>
                                    <Info fontSize="small" sx={{ color: 'gray', cursor: 'pointer' }} />
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() =>
                                    setEditingReason(prev => ({ ...prev, [invoice.id]: true }))
                                  }
                                >
                                  Add reason
                                </Button>
                              )}
                            </TableCell>



                            <TableCell>£{safeToFixed(invoice.totalPrice)}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: 'green' }}>
                              £{safeToFixed(rowDiscountedTotal)}
                            </TableCell>

                            <TableCell className="hide-in-pdf" >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={saveRowDiscount}
                                disabled={rowSaveStatus[invoice.id] === 'saving'}
                              >
                                {rowSaveStatus[invoice.id] === 'saving'
                                  ? 'Saving...'
                                  : rowSaveStatus[invoice.id] === 'saved'
                                    ? 'Saved ✓'
                                    : 'Save'}
                              </Button>

                            </TableCell>
                          </TableRow>
                        )
                      })}
                    <TableRow sx={{ backgroundColor: alpha(appTheme.colors.primary, 0.08) }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                        TOTAL
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700 }}>
                        {groupTotals.items}
                      </TableCell>

                      <TableCell colSpan={3} />


                      <TableCell sx={{ fontWeight: 700 }}>
                        £{safeToFixed(groupTotals.original)}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700, color: appTheme.colors.success }}>
                        £{safeToFixed(groupTotals.discounted)}
                      </TableCell>

                      <TableCell />
                    </TableRow>

                  </TableBody>

                </Table>
              </StyledTableContainer>
            </Box>
          )}
        </DialogContent>

        {/* **MODAL ACTIONS** */}
        {/* **MODAL ACTIONS - Both Send & Download** */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          padding: appTheme.spacing.lg,
          backgroundColor: appTheme.colors.surfaceElevated,
          borderTop: `1px solid ${appTheme.colors.borderPrimary}`,
        }}>
          {/* Send Email Button */}
          <ActionButton
            onClick={() => setEmailDialog({
              open: true,
              email: selectedRestaurant?.email || "", // Default to customer's email
              message: "",
            })}
            variant="contained"
            disabled={emailSending}
            sx={{
              backgroundColor: appTheme.colors.secondary,
              '&:hover': { backgroundColor: appTheme.colors.warmCopper }
            }}
          >
            <Send size={18} style={{ marginRight: 8 }} />
            Send to Customer
          </ActionButton>

          {/* Download PDF Button */}
          <ActionButton
            onClick={handleDownloadPDF}
            variant="contained"
            disabled={isPrinting}
            sx={{
              backgroundColor: appTheme.colors.primary,
              '&:hover': { backgroundColor: appTheme.colors.deepTeal }
            }}
          >
            {isPrinting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Generating...
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Download size={18} />
                Download PDF
              </Box>
            )}
          </ActionButton>
        </Box>

      </Dialog>
    </PageContainer>
  );
};

export default ConsolidatedInvoiceView;
